import * as React from "react";
import {
  Responsive,
  WidthProvider,
  Layout as GridLayout
} from "react-grid-layout";
import _ from "lodash";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import { ReactGridLayoutStyles } from "src/library/components/GlobalStyles/ReactGridLayoutStyles";
import "react-grid-layout/css/styles.css";
import "react-grid-layout/node_modules/react-resizable/css/styles.css";
import {
  DashboardMode,
  dashboardLayoutConfig,
  useDashboardContext
} from "src/core/dashboard/contexts/DashboardContext";
import { useLocalStorage } from "src/library/hooks";
import { IStore } from "src/core/shared/store";

import {
  GetWidgetFiltersParam,
  WidgetSlug
} from "../../api/widget-data.api";
import {
  WidgetDisplayTypes,
  IWidget
} from "../../api/dashboard.api";
import {
  OrderTotalsByOriginWidget,
  OrderTotalsByTypeWidget,
  OrderTotalsWidget,
  CreditTopOffendersWidget,
  SupportTopOffendersWidget,
  TotalTicketsWidget,
  TotalTicketsByStatusWidget,
  TotalTicketsByTypeWidget,
  TotalTimeSpentWidget,
  PendingCustomersWidget,
  AccountingDashboardWidget
} from "../widgets";
import {
  WidgetProps,
  getWidgetFilterParams
} from "../widgets/utils";
import { DashboardWidgetContainer } from "../DashboardWidgetContainer";
import { RealTimeDataWidget } from "../widgets/real-time-data";
import { scrollToLowestWidget } from "../../utils";

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Responsive Admin Dashboard Grid View
 * Renders all dashboard widgets in a responsive customisable grid
 */
const DashboardGridLayout: React.FC = () => {
  const {
    globalFilters,
    mode,
    layoutsAndWidgets,
    userEditedLayout,
    updateCurrentLayout,
    updateUserEditedLayout,
    setContainerWidth,
    removeWidget,
    fullScreenState
  } = useDashboardContext();

  const [ currentBreakpoint, setCurrentBreakpoint ] = React.useState<string>("lg");
  const [ widgetFilterParams, setWidgetFilterParams ] = React.useState<GetWidgetFiltersParam | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [ filtersChanged, setFiltersChanged ] = React.useState<boolean>(false);
  const [ dashboardGlobalFilterParams, setDashboardGlobalFilterParams ] = useLocalStorage("dashboard-global-filter-params", "{}");
  // Used to determine if an updated layout has a new widget in
  const [ countOfLayoutItems, setCountOfLayoutItems ] = React.useState<number>(0);

  const { terminology, allowPendingCustomers } = useMappedState(({ global }: IStore) => ({
    terminology: global.settings?.terminology,
    allowPendingCustomers: global.settings?.allowPendingCustomers
  }));

  React.useEffect(() => {
    // Set inital count of widgets if not already set
    if (countOfLayoutItems === 0 && layoutsAndWidgets?.widgets.length) {
      setCountOfLayoutItems(layoutsAndWidgets.widgets.length);
    }
  }, [ countOfLayoutItems, layoutsAndWidgets.widgets.length ]);

  // Build out the filters each widget will need
  React.useEffect(() => {
    if (Object.keys(globalFilters).length) {
      const transformedFilters = getWidgetFilterParams(globalFilters);

      if (JSON.stringify(transformedFilters) !== JSON.stringify(widgetFilterParams)) {
        setWidgetFilterParams(transformedFilters);
      }

      // Update state managing if filters changed, which will inform cache behaviour on widgets
      if (transformedFilters) {
        if (JSON.stringify(transformedFilters) !== dashboardGlobalFilterParams) {
          // Update local storage
          setDashboardGlobalFilterParams(transformedFilters ? JSON.stringify(transformedFilters) : "{}");

          // Update filters changed to true if not already
          if (!filtersChanged) {
            setFiltersChanged(true);
          }
        }
      }
    }
  }, [
    dashboardGlobalFilterParams,
    filtersChanged,
    globalFilters,
    setDashboardGlobalFilterParams,
    widgetFilterParams
  ]);

  const handleLayoutChange = React.useCallback((_current: GridLayout[]) => {
    if (!userEditedLayout) {
      updateUserEditedLayout(true);
    }

    const currentLayoutLength = layoutsAndWidgets?.layouts?.lg.length || 0;

    // If there are more widgets than before,
    // we've added a new widget and so scroll to lowest bottom.
    // Don't scroll to bottom if countOfLayoutItems hasn't been set yet
    if (currentLayoutLength > countOfLayoutItems && countOfLayoutItems !== 0) {
      // update count of layout items
      setCountOfLayoutItems(currentLayoutLength);
      scrollToLowestWidget();
    }
  }, [
    userEditedLayout,
    layoutsAndWidgets.layouts,
    countOfLayoutItems,
    updateUserEditedLayout
  ]);

  React.useEffect(() => {
    if (fullScreenState.active) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [ fullScreenState.active ]);

  React.useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [ setContainerWidth ]);

  // Based on widget slug, return the relevant widget, ensuring to pass global filters and widget options
  const generateWidgetComponent = React.useCallback((widget: IWidget) => {
    // Ensure filter params set before continuing as these are used when requesting widget data from API
    if (!widgetFilterParams) {
      return;
    }

    // This should not happen however, if widgetUserOptions ever is undefined, currently this will cause the platform to infinitely reload.
    if (!widget.options) {
      // Set a default to the required options prop to mitigate infinite reload.
      widget.options = { view: WidgetDisplayTypes.LineGraph };
    }

    // The props to pass to each widget component.
    const widgetProps: WidgetProps = {
      options: widget.options,
      filters: widgetFilterParams,
      widgetTypeId: widget.widgetTypeId,
      widgetId: typeof widget.id === "string" ? parseInt(widget.id, 10) : widget.id,
      ignoreCache: filtersChanged,
      terminology: terminology || undefined
    };

    switch (widget.widgetType?.slug) {
      case WidgetSlug.OrderTotal:
        return (<OrderTotalsWidget {...widgetProps} />);
      case WidgetSlug.OrderTotalByType:
        return (<OrderTotalsByTypeWidget {...widgetProps} />);
      case WidgetSlug.OrderTotalByOrigin:
        return (<OrderTotalsByOriginWidget {...widgetProps} />);
      case WidgetSlug.CreditTopOffenders:
        return (<CreditTopOffendersWidget {...widgetProps} />);
      case WidgetSlug.TotalTickets:
        return (<TotalTicketsWidget {...widgetProps} />);
      case WidgetSlug.TotalTicketsByStatus:
        return (<TotalTicketsByStatusWidget {...widgetProps} />);
      case WidgetSlug.TotalTicketsByType:
        return (<TotalTicketsByTypeWidget {...widgetProps} />);
      case WidgetSlug.TotalTimeSpent:
        return (<TotalTimeSpentWidget {...widgetProps} />);
      case WidgetSlug.SupportTopOffenders:
        return (<SupportTopOffendersWidget {...widgetProps} />);
      case WidgetSlug.RealTimeData:
        return (<RealTimeDataWidget />);
      case WidgetSlug.PendingCustomer:
        if (!allowPendingCustomers) return null;

        return (<PendingCustomersWidget {...widgetProps} />);
      case WidgetSlug.AccountingOverview:
        return (<AccountingDashboardWidget />);
      default:
        return `Unhandled Widget: ${widget.widgetType?.slug}`;
    }
  }, [
    allowPendingCustomers,
    filtersChanged,
    terminology,
    widgetFilterParams
  ]);

  // Removes widget from local state of current dashboard. Does not affect persisted dashboard in db.
  const handleRemoveWidget = React.useCallback((widgetToRemoveId: number | string) => {
    setCountOfLayoutItems(prevCount => prevCount - 1);
    removeWidget(widgetToRemoveId);
  }, [ removeWidget ]);

  return (
    <StyledWrapper
      ref={containerRef}
      mode={mode}
    >
      <ReactGridLayoutStyles />

      {currentBreakpoint && layoutsAndWidgets.layouts && layoutsAndWidgets.layouts[ currentBreakpoint ] && (
        <ResponsiveGridLayout
          allowOverlap={false}
          autoSize
          containerPadding={[ 0, 0 ]}
          margin={[ dashboardLayoutConfig.margin, dashboardLayoutConfig.margin ]}
          draggableHandle=".widget-drag"
          breakpoints={{
            lg: 900,
            md: 550,
            sm: 400
          }}
          cols={dashboardLayoutConfig.cols}
          onLayoutChange={handleLayoutChange}
          onBreakpointChange={currentBreakpoint => setCurrentBreakpoint(currentBreakpoint)}
          className="layout"
          layouts={layoutsAndWidgets.layouts}
          onDragStop={gridProps => updateCurrentLayout(currentBreakpoint, gridProps)}
          onResizeStop={gridProps => updateCurrentLayout(currentBreakpoint, gridProps)}
          compactType="vertical"
          rowHeight={dashboardLayoutConfig.rowHeight}
          isResizable={mode === "edit"}
        >
          {currentBreakpoint && layoutsAndWidgets.layouts[ currentBreakpoint ].map(layoutItem => {
            const widget = _.find(layoutsAndWidgets.widgets, w => w.id.toString() === layoutItem.i);

            if (!widget) {
              return null;
            }

            return (
              <div
                key={layoutItem.i}
                data-grid={{
                  w: layoutItem.w,
                  h: layoutItem.h,
                  x: layoutItem.x,
                  y: layoutItem.y
                }}
              >
                <DashboardWidgetContainer
                  mode={mode}
                  onDelete={() => handleRemoveWidget(widget.id)}
                  title={widget.widgetType?.slug === WidgetSlug.RealTimeData ? "Live Totals" : undefined}
                >
                  {widget.widgetType?.slug && generateWidgetComponent(widget)}
                </DashboardWidgetContainer>
              </div>
            );
          })}

        </ResponsiveGridLayout>
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div<{ mode: DashboardMode }>`
  ${({ theme, mode }) => css`
    margin-top: ${mode === DashboardMode.Edit ? theme.spacing.margins.lg : ""};
  `}
`;

export default DashboardGridLayout;
